import { useQuery } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import type { PartyInviteResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

const PARTY_INVITES_FOREGROUND_REFRESH_MS = 15_000;

/**
 * A party's OUTGOING pending invites (GET /queue/party/{partyId}/invites).
 *
 * The leader management view: the invites the party SENT that are still pending,
 * each enriched server-side with `invitee_display_name`. Backs the party-detail
 * "Invited (pending) · Cancel" list. Keyed by the party id (its own cache slot,
 * distinct from `party.detail`'s roster) so a failing invites fetch never blanks
 * the roster. Disabled until a partyId is present. The list is server truth —
 * the page renders it verbatim; an invite / cancel mutation invalidates
 * `party.sentInvites(partyId)` to refetch. Only the party leader owns this
 * management projection; callers must keep the query disabled for members.
 */
export function usePartyInvites(partyId: string, enabled: boolean = true) {
  return useQuery<PartyInviteResponse[]>({
    queryKey: queryKeys.party.sentInvites(partyId),
    queryFn: () => queueApi.listPartyInvites(partyId),
    enabled: Boolean(partyId) && enabled,
    refetchInterval: PARTY_INVITES_FOREGROUND_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnMount: "always",
  });
}
